// New Job Opening modal — single-form layout with two logical
// sections (Job Details, Job Description + Skills). The JD section
// accepts either typed text or an uploaded PDF; when a PDF is
// uploaded, we extract text via pdfPosExtract then ask Claude to
// auto-populate the required/nice-to-have skills tags.
//
// Admin-only — the route guard upstream already enforces this.
// This component just renders the form.

import { useRef, useState } from 'react'
import {
  X, Upload, Loader, Save, Plus, Sparkles,
} from 'lucide-react'
import { extractCVText, extractJobRequirements } from '../../lib/cvParser'
import { saveJobOpening, DEPARTMENTS } from '../../lib/recruitmentStore'

const HIDDEN_INPUT_STYLE = {
  position: 'absolute', width: '1px', height: '1px',
  opacity: 0, overflow: 'hidden', pointerEvents: 'none',
}

export default function NewOpeningModal({ branch, onClose, onSaved, addToast }) {
  const fileRef = useRef(null)
  const [jdTab, setJdTab] = useState('type') // 'type' | 'upload'
  const [saving, setSaving] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [extractedFrom, setExtractedFrom] = useState(null) // filename if PDF was used

  const [form, setForm] = useState({
    jobTitle: '',
    department: 'Kitchen',
    preferredAgeMin: 18,
    preferredAgeMax: 45,
    preferredLocation: '',
    jobDescription: '',
    requiredSkills: [],
    niceToHaveSkills: [],
    minExperienceYears: 0,
    minEducation: '',
  })

  // Tag inputs — one ephemeral draft each
  const [requiredDraft, setRequiredDraft] = useState('')
  const [niceDraft, setNiceDraft] = useState('')

  function updateField(key, value) {
    setForm(f => ({ ...f, [key]: value }))
  }

  function addTag(field, value) {
    const v = String(value || '').trim()
    if (!v) return
    setForm(f => {
      if ((f[field] || []).includes(v)) return f
      return { ...f, [field]: [...(f[field] || []), v] }
    })
  }

  function removeTag(field, tag) {
    setForm(f => ({ ...f, [field]: (f[field] || []).filter(t => t !== tag) }))
  }

  async function handleJdPdf(file) {
    if (!file) return
    setExtracting(true)
    setExtractedFrom(null)
    try {
      const text = await extractCVText(file)
      updateField('jobDescription', text)
      setExtractedFrom(file.name)

      // Auto-populate requirements via local keyword detection
      const reqs = extractJobRequirements(text)
      if (reqs) {
        setForm(f => ({
          ...f,
          jobTitle: f.jobTitle || reqs.suggestedJobTitle || f.jobTitle,
          department: reqs.suggestedDepartment && DEPARTMENTS.includes(reqs.suggestedDepartment) ? reqs.suggestedDepartment : f.department,
          requiredSkills: Array.isArray(reqs.requiredSkills) && reqs.requiredSkills.length > 0 ? reqs.requiredSkills : f.requiredSkills,
          niceToHaveSkills: Array.isArray(reqs.niceToHaveSkills) && reqs.niceToHaveSkills.length > 0 ? reqs.niceToHaveSkills : f.niceToHaveSkills,
          minExperienceYears: Number(reqs.minExperience) || f.minExperienceYears,
          minEducation: reqs.minEducation || f.minEducation,
        }))
        if (reqs.requiredSkills.length > 0) {
          addToast?.(`Auto-detected ${reqs.requiredSkills.length} required skill${reqs.requiredSkills.length === 1 ? '' : 's'} from job description`, 'success')
        }
      }
    } catch (err) {
      addToast?.(`Failed to read PDF: ${err.message}`, 'error')
    } finally {
      setExtracting(false)
    }
  }

  async function handleSave() {
    if (!form.jobTitle.trim()) {
      addToast?.('Job title is required', 'error')
      return
    }
    if (!form.jobDescription || form.jobDescription.trim().length < 30) {
      addToast?.('Job description must be at least 30 characters', 'error')
      return
    }
    if (form.preferredAgeMin > form.preferredAgeMax) {
      addToast?.('Min age cannot exceed max age', 'error')
      return
    }

    setSaving(true)
    try {
      const saved = await saveJobOpening({
        branch,
        jobTitle: form.jobTitle.trim(),
        department: form.department,
        jobDescription: form.jobDescription,
        requiredSkills: form.requiredSkills,
        niceToHaveSkills: form.niceToHaveSkills,
        preferredAgeMin: form.preferredAgeMin,
        preferredAgeMax: form.preferredAgeMax,
        preferredLocation: form.preferredLocation.trim(),
        minExperienceYears: form.minExperienceYears,
        minEducation: form.minEducation,
      })
      addToast?.('Job opening created', 'success')
      onSaved?.(saved)
      onClose?.()
    } catch (err) {
      addToast?.(`Failed to save: ${err.message}`, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-bold text-white">New Job Opening</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Create a job opening for the <span className="text-brand-400 capitalize">{branch}</span> branch
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* ── Job Details ── */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Job Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Job Title</label>
                <input
                  type="text"
                  value={form.jobTitle}
                  onChange={e => updateField('jobTitle', e.target.value)}
                  placeholder="e.g. Senior Kitchen Manager"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Department</label>
                <select
                  value={form.department}
                  onChange={e => updateField('department', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                >
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Preferred Age Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="16" max="80"
                    value={form.preferredAgeMin}
                    onChange={e => updateField('preferredAgeMin', Number(e.target.value) || 0)}
                    className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
                  />
                  <span className="text-gray-500 text-xs">to</span>
                  <input
                    type="number" min="16" max="80"
                    value={form.preferredAgeMax}
                    onChange={e => updateField('preferredAgeMax', Number(e.target.value) || 0)}
                    className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Preferred Location</label>
                <input
                  type="text"
                  value={form.preferredLocation}
                  onChange={e => updateField('preferredLocation', e.target.value)}
                  placeholder={branch === 'limpopo' ? 'e.g. Makhado, Limpopo' : 'e.g. Pretoria'}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Min Experience (years)</label>
                <input
                  type="number" min="0" max="40"
                  value={form.minExperienceYears}
                  onChange={e => updateField('minExperienceYears', Number(e.target.value) || 0)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Min Education</label>
                <input
                  type="text"
                  value={form.minEducation}
                  onChange={e => updateField('minEducation', e.target.value)}
                  placeholder="e.g. Matric, Diploma"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
          </section>

          {/* ── Job Description ── */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Job Description</h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setJdTab('type')}
                  className={`px-2.5 py-1 text-xs rounded ${jdTab === 'type' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  Type
                </button>
                <button
                  onClick={() => setJdTab('upload')}
                  className={`px-2.5 py-1 text-xs rounded ${jdTab === 'upload' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  Upload PDF
                </button>
              </div>
            </div>

            {jdTab === 'upload' && (
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={extracting}
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-200 px-3 py-2 rounded text-sm border border-gray-700"
                >
                  {extracting ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                  {extracting ? 'Analysing...' : 'Choose JD PDF'}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,application/pdf,application/x-pdf"
                  onChange={e => handleJdPdf(e.target.files?.[0])}
                  style={HIDDEN_INPUT_STYLE}
                />
                {extractedFrom && !extracting && (
                  <p className="text-[11px] text-green-400 mt-1.5 flex items-center gap-1">
                    <Sparkles size={11} /> Extracted from <span className="font-mono">{extractedFrom}</span>
                  </p>
                )}
              </div>
            )}

            <textarea
              rows={6}
              value={form.jobDescription}
              onChange={e => updateField('jobDescription', e.target.value)}
              placeholder="Paste or type the full job description here…"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white font-mono"
            />
          </section>

          {/* ── Required Skills ── */}
          <section className="space-y-2">
            <label className="block text-xs text-gray-400">
              Required Skills
              {extractedFrom && form.requiredSkills.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-brand-400">
                  <Sparkles size={10} /> Auto-detected
                </span>
              )}
            </label>
            <TagInput
              tags={form.requiredSkills}
              draft={requiredDraft}
              setDraft={setRequiredDraft}
              onAdd={v => addTag('requiredSkills', v)}
              onRemove={t => removeTag('requiredSkills', t)}
              placeholder="Type a skill and press Enter"
            />
          </section>

          <section className="space-y-2">
            <label className="block text-xs text-gray-400">Nice-to-have Skills (optional)</label>
            <TagInput
              tags={form.niceToHaveSkills}
              draft={niceDraft}
              setDraft={setNiceDraft}
              onAdd={v => addTag('niceToHaveSkills', v)}
              onRemove={t => removeTag('niceToHaveSkills', t)}
              placeholder="Type a skill and press Enter"
            />
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || extracting}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white px-4 py-2 rounded text-sm"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Save Opening
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Sub-component: tag input ─────────────────────────────────────

function TagInput({ tags, draft, setDraft, onAdd, onRemove, placeholder }) {
  function handleKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (draft.trim()) { onAdd(draft); setDraft('') }
    } else if (e.key === 'Backspace' && !draft && tags.length > 0) {
      onRemove(tags[tags.length - 1])
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 min-h-[38px]">
      {tags.map(tag => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 bg-gray-700 text-gray-200 text-xs px-2 py-0.5 rounded"
        >
          {tag}
          <button onClick={() => onRemove(tag)} className="text-gray-400 hover:text-white">
            <X size={11} />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tags.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[140px] bg-transparent text-xs text-white placeholder:text-gray-500 outline-none"
      />
      {draft.trim() && (
        <button onClick={() => { onAdd(draft); setDraft('') }} className="text-xs text-brand-400 hover:text-brand-300">
          <Plus size={12} />
        </button>
      )}
    </div>
  )
}
